import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Canvas } from '@react-three/fiber';
import VideoScene from '../VideoScene';
import BackgroundScene from '../BackgroundScene';
import styles from './MainScene.module.css';
import FeatureButton from '../FeatureButton';
import { usePlausible } from '../../hooks/usePlausible';
import { NEWSLETTER_SUBSCRIBE, DISCORD_JOIN, CATEGORIES, createEventProps } from '../../utils/analytics';
import Loader from '../Loader';
import { debounce, addPassiveEventListener, throttle } from '../../utils/performanceUtils';
import FeatureSection from '../FeatureSection';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

interface MainSceneProps {
  onLoadComplete?: () => void;
}

export const MainScene: React.FC<MainSceneProps> = memo(({ onLoadComplete }) => {
  const scene1Ref = useRef<HTMLDivElement>(null);
  const mainHeroTitleRef = useRef<HTMLHeadingElement>(null);
  const taglineRef = useRef<HTMLParagraphElement>(null);
  const featureButtonRef = useRef<HTMLDivElement>(null);
  const featureSectionRef = useRef<HTMLDivElement>(null);
  const storyText1Ref = useRef<HTMLParagraphElement>(null);
  const storyText2Ref = useRef<HTMLParagraphElement>(null);
  const storyText3Ref = useRef<HTMLParagraphElement>(null);
  const joinButtonRef = useRef<HTMLAnchorElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [iframeHeight, setIframeHeight] = useState(220);
  const [isLoading, setIsLoading] = useState(true);
  const trackEvent = usePlausible();

  // Track Discord join clicks
  const handleJoinClick = useCallback(() => {
    trackEvent(DISCORD_JOIN, createEventProps(CATEGORIES.STORY));
  }, [trackEvent]);

  // Set proper iframe height based on screen size
  useEffect(() => {
    // Debounce resize handler to prevent excessive recalculations
    const updateIframeHeight = debounce(() => {
      // Adjust height for mobile devices
      if (window.innerWidth <= 480) {
        setIframeHeight(280);
      } else if (window.innerWidth <= 768) {
        setIframeHeight(260);
      } else {
        setIframeHeight(220);
      }
    }, 150);

    // Initial call
    updateIframeHeight();

    // Add resize listener with passive option
    if (typeof window !== 'undefined') {
      addPassiveEventListener(window, 'resize', updateIframeHeight as EventListener);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateIframeHeight as EventListener);
    };
  }, []);

  // Handle loading state with window load event only
  useEffect(() => {
    // Wait for window to load all assets
    const handleLoad = () => {
      // Use a small setTimeout to prevent layout work right after load event
      setTimeout(() => {
        setIsLoading(false);
        if (onLoadComplete) {
          onLoadComplete();
        }
      }, 10);
    };
    
    // Add event listener for window load
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }
    
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [onLoadComplete]);

  useEffect(() => {
    if (isLoading) return; // Skip animations if still loading

    // Force initial visibility
    const elements = [
      mainHeroTitleRef.current,
      taglineRef.current,
      featureButtonRef.current
    ].filter(Boolean); // Filter out null elements
    
    // Batch DOM operations to avoid layout thrashing
    elements.forEach(el => {
      if (el) {
        el.style.opacity = '0';
        el.style.visibility = 'visible';
      }
    });

    // Use requestAnimationFrame to ensure animations happen on next paint
    let animationId = requestAnimationFrame(() => {
      // Animate hero text on mount with guaranteed visibility
      const tl = gsap.timeline({ 
        defaults: { ease: 'power2.out' },
        onStart: () => {
          // Ensure elements are visible for GSAP to animate them
          elements.forEach(el => {
            if (el) el.style.visibility = 'visible';
          });
        }
      });
      
      // Main hero title animation
      if (mainHeroTitleRef.current) {
        tl.fromTo(
          mainHeroTitleRef.current,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, delay: 0.2 }
        );
      }
      
      // Tagline animation
      if (taglineRef.current) {
        tl.fromTo(
          taglineRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8 },
          '-=0.5' // Slightly overlap with previous animation
        );
      }
      
      // Feature button animation
      if (featureButtonRef.current) {
        tl.fromTo(
          featureButtonRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8 },
          '-=0.6' // More overlap for a faster sequence
        );
      }
    });

    // Create scroll trigger for video animation and text fade
    // Only create if the trigger element exists
    let trigger: ScrollTrigger | undefined;
    if (scene1Ref.current && elements.length > 0) {
      // Throttle scroll updates for better performance
      const handleScrollUpdate = throttle((self: ScrollTrigger) => {
        setScrollProgress(self.progress);
        // Fade out text as user scrolls
        if (elements.length > 0) {
          gsap.to(elements, {
            opacity: 1 - Math.pow(self.progress, 1.5),
            duration: 0
          });
        }
      }, 16); // Aim for 60fps (1000ms / 60 ≈ 16ms)
      
      trigger = ScrollTrigger.create({
        trigger: scene1Ref.current,
        start: "top 20%",
        end: "bottom -100%",
        scrub: 1.5,
        onUpdate: handleScrollUpdate
      });
    }

    // Feature section animation trigger
    let featureTimeline: gsap.core.Timeline | undefined;
    if (featureSectionRef.current) {
      featureTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: featureSectionRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      });
    }

    // Only create the story timeline if the trigger element exists
    let storyTimeline: gsap.core.Timeline | undefined;
    if (storyText1Ref.current) {
      // Create a better ScrollTrigger for the story section
      storyTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: storyText1Ref.current,
          start: "top 85%", // Start animation earlier when element is 85% from top of viewport
          end: "center 60%", // End animation when element's center is at 60% of viewport
          toggleActions: "play none none reverse", // Play on enter, reverse on exit
          once: false, // Ensure it triggers every time it enters the viewport
          markers: false // For debugging only, remove in production
        }
      });

      // Animate first story text with a fade up effect
      if (storyText1Ref.current) {
        storyTimeline.fromTo(
          storyText1Ref.current,
          { 
            opacity: 0,
            y: 30
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out"
          }
        );
      }

      // Animate second story text with a slight delay
      if (storyText2Ref.current) {
        storyTimeline.fromTo(
          storyText2Ref.current,
          { 
            opacity: 0,
            y: 30
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out"
          },
          "-=0.6" // Slight overlap with previous animation
        );
      }

      // Animate third story text with a slight delay
      if (storyText3Ref.current) {
        storyTimeline.fromTo(
          storyText3Ref.current,
          { 
            opacity: 0,
            y: 30
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out"
          },
          "-=0.6" // Slight overlap with previous animation
        );
      }

      // Animate join button last
      if (joinButtonRef.current) {
        storyTimeline.fromTo(
          joinButtonRef.current,
          { 
            opacity: 0,
            y: 30
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out"
          },
          "-=0.6" // Slight overlap with previous animation
        );
      }
    }

    return () => {
      // Clean up animations
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      
      if (trigger) {
        trigger.kill();
      }
      
      if (featureTimeline) {
        featureTimeline.kill();
      }
      
      if (storyTimeline) {
        storyTimeline.kill();
      }
    };
  }, [isLoading]);

  // Removed loading animation completely

  return (
    <div className={styles.mainSceneContainer}>
      <BackgroundScene modelScale={1.8} />
      <div className={styles.sceneSections}>
        <section id="scene1" className={styles.sceneSection}>
          <div className={styles.sceneContent}>
            <div className={styles.heroText}>
              <h1>
                Born on <span>Discord,</span> Made for <span>Music Together</span>
              </h1>
              <p ref={taglineRef} className={styles.tagline}>
                The Rythm bot you know and love + an all new activity. The best way to listen with friends.
              </p>
              <div ref={featureButtonRef} className={styles.featureButtonContainer}>
                <FeatureButton />
              </div>
            </div>
            <div className={styles.textContainer} ref={scene1Ref}>
              {/* Additional text content can go here */}
            </div>
            <div className={styles.videoContainer}>
              <Canvas camera={{ position: [0, 0, 12], fov: 50 }}>
                <VideoScene 
                  position={[0, 0, 0]} 
                  scale={[0.5, 0.5, 0.5]}
                  scrollProgress={scrollProgress}
                />
              </Canvas>
            </div>
          </div>
        </section>
      </div>
      
      <div ref={featureSectionRef} className={styles.featureSectionWrapper}>
        <FeatureSection />
      </div>
      
      <div className={styles.storyContainer}>
        <h2 ref={storyText1Ref} className={styles.storyText1}>
          We built Rythm for us — music lovers on Discord who wanted to listen together. It spread to over 20 million servers.  
        </h2>
        <p ref={storyText2Ref} className={styles.storyText2}>
          Then YouTube shut it down. Most would've walked away. We rebuilt it. 
        </p>
        <p ref={storyText3Ref} className={styles.storyText3}>
          Today, Rythm is fully licensed, back on Discord, and ready to move music forward — the way it should've always been.
        </p>
        <div className={styles.joinButtonContainer}>
          <a 
            ref={joinButtonRef} 
            href="https://discord.gg/ZtH6rZpa8y"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.joinButton}
            onClick={handleJoinClick}
          >
            Join Rythm
          </a>
        </div>
      </div>
    </div>
  );
});

export default MainScene; 